import { StandardLayout } from '@sequencehq/core-components'
import { BillingScheduleHomeHeader } from './BillingScheduleHomeHeader'
import { BillingScheduleHomeContent } from './BillingScheduleHomeContent/BillingScheduleHomeContent'
import { Navigate, Outlet } from 'react-router-dom'
import { Provider } from 'jotai'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import NotFound from 'components/ErrorPages/NotFound'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { usePermissions } from 'lib/rbac'
import { BillingScheduleHomeInspector } from './BillingScheduleHomeContent/inspector/BillingScheduleHomeInspector'

const useScheduleHomeRoot = () => {
  const billingScheduleId = useBillingScheduleId()
  const scheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId,
    select: data => {
      return {
        isDraft: data.billingSchedule.status === 'DRAFT'
      }
    }
  })

  return {
    isNotFound:
      scheduleAggregateQuery.isError &&
      scheduleAggregateQuery.error.status === 404,
    /**
     *  By looking at isFetching, we don't erroneously indicate a newly active schedule, that
     *  may be in the process of loading in, is a draft.
     **/
    isDraft:
      !scheduleAggregateQuery.isFetching &&
      scheduleAggregateQuery.data?.isDraft,
    billingScheduleId
  }
}

export const BillingScheduleHome = () => {
  const { isNotFound, isDraft, billingScheduleId } = useScheduleHomeRoot()
  const { can } = usePermissions()

  if (isNotFound) {
    return <NotFound />
  }

  /**
   * We don't want to show the billing schedule home when the billing schedule is a draft.
   * This conditional serves as a fallback for any links, or direct access, which point to
   * schedule home for a draft.
   *
   * Since Cube doesn't make use of the schedule aggregate, yet, this navigation isn't overly
   * speedy, so we should look to target the correct page where possible, for now. When Cube
   * uses the aggregate it'll be much of a muchness as the data will already be in the cache, making
   * the redirect super speedy.
   */
  if (isDraft && can('org.billing:update')) {
    return (
      <Navigate replace to={`/billing-schedules/${billingScheduleId}/edit`} />
    )
  }

  return (
    <Provider>
      <StandardLayout
        header={<BillingScheduleHomeHeader />}
        content={<BillingScheduleHomeContent />}
        inspector={<BillingScheduleHomeInspector />}
      />
      <Outlet />
    </Provider>
  )
}
